import { AccessTimeOutlined, ArticleOutlined, LockOutlined, SearchOutlined } from '@mui/icons-material'
import {
  Box,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { FC, Fragment, ReactNode, useEffect, useMemo, useState } from 'react'
import { useDebounce } from 'use-debounce'
import {
  DocPageScope,
  DocPageStatus,
  useRecentDocPagesQuery,
  useSearchDocPagesQuery,
} from '../../../generated'
import { GRAPHQL_QUERY_POLICY } from '../../constants'
import { DOCS_LABELS } from './docs.constants'
import { PaginationWithJump } from '../common/PaginationWithJump'

interface DocsHomeProps {
  scope: DocPageScope
  onOpen: (slug: string) => void
}

const SEARCH_LIMIT = 10

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/** Splits text on the query term and wraps matches in <strong> for highlighting. */
const highlight = (text: string, query: string): ReactNode => {
  const term = query.trim()
  if (!term) return text
  const parts = text.split(new RegExp(`(${escapeRegExp(term)})`, 'ig'))
  return parts.map((part, i) =>
    part.toLowerCase() === term.toLowerCase() ? (
      // eslint-disable-next-line react/no-array-index-key
      <Box key={i} component="strong" sx={{ color: 'primary.main', fontWeight: 700 }}>
        {part}
      </Box>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    ),
  )
}

const fullName = (user?: { firstName?: string | null; lastName?: string | null } | null) =>
  user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : ''

export const DocsHome: FC<DocsHomeProps> = ({ scope, onOpen }) => {
  const [input, setInput] = useState('')
  const [query] = useDebounce(input, 350)
  const [page, setPage] = useState(1)

  // Reset to the first page whenever the query or scope changes.
  useEffect(() => setPage(1), [query, scope])

  const searching = query.trim().length > 0

  const { data, loading } = useSearchDocPagesQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: { searchDocPagesInput: { query: query.trim(), scope, page, limit: SEARCH_LIMIT } },
    skip: !searching,
  })

  const { data: recentData } = useRecentDocPagesQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: { scope, limit: 5 },
  })

  const results = useMemo(() => (data?.searchDocPages?.results || []).filter(Boolean), [data])
  const total = data?.searchDocPages?.total || 0
  const pageCount = Math.ceil(total / SEARCH_LIMIT)
  const recent = useMemo(() => (recentData?.recentDocPages?.docPages || []).filter(Boolean), [recentData])

  const renderResults = () => {
    if (!searching) {
      return (
        <Stack alignItems="center" spacing={1.5} sx={{ py: 8, color: 'text.secondary' }}>
          <SearchOutlined sx={{ fontSize: 40, opacity: 0.4 }} />
          <Typography variant="body2">{DOCS_LABELS.SEARCH_PROMPT}</Typography>
        </Stack>
      )
    }
    if (loading && !results.length) {
      return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      )
    }
    if (!results.length) {
      return (
        <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
          {DOCS_LABELS.SEARCH_NO_RESULTS}
        </Typography>
      )
    }
    return (
      <>
        <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>
          {total} {DOCS_LABELS.SEARCH_RESULTS_COUNT}
          {total === 1 ? '' : 's'}
        </Typography>
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          {results.map((result) => {
            const docPage = result?.page
            if (!docPage) return null
            return (
              <Card key={docPage.id} variant="outlined" sx={{ borderRadius: 2 }}>
                <CardActionArea onClick={() => onOpen(docPage.slug)} sx={{ p: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <ArticleOutlined fontSize="small" sx={{ color: 'text.disabled' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {docPage.icon ? `${docPage.icon} ` : ''}
                      {highlight(docPage.title || DOCS_LABELS.UNTITLED, query)}
                    </Typography>
                    {docPage.scope === DocPageScope.Personal && (
                      <Chip
                        size="small"
                        icon={<LockOutlined sx={{ fontSize: 12 }} />}
                        label={DOCS_LABELS.VISIBILITY_PRIVATE}
                        variant="outlined"
                        sx={{ height: 20 }}
                      />
                    )}
                    {docPage.status !== DocPageStatus.Published && (
                      <Chip
                        size="small"
                        label={
                          docPage.status === DocPageStatus.Draft
                            ? DOCS_LABELS.DRAFT_BADGE
                            : DOCS_LABELS.ARCHIVED_BADGE
                        }
                        color={docPage.status === DocPageStatus.Draft ? 'warning' : 'default'}
                        sx={{ height: 20 }}
                      />
                    )}
                  </Stack>
                  {result?.snippet && (
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {highlight(result.snippet, query)}
                    </Typography>
                  )}
                </CardActionArea>
              </Card>
            )
          })}
        </Stack>
        {pageCount > 1 && (
          <PaginationWithJump
            count={pageCount}
            page={page}
            onChange={setPage}
            sx={{ justifyContent: 'center', pt: 0, pb: 0, mt: 3 }}
          />
        )}
      </>
    )
  }

  return (
    <Box sx={{ maxWidth: 1040, mx: 'auto', width: '100%' }}>
      <Typography variant="h4" sx={{ fontWeight: 700 }}>
        {DOCS_LABELS.HOME_TITLE}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {DOCS_LABELS.HOME_SUBTITLE}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          gap: 3,
          alignItems: 'flex-start',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          <TextField
            fullWidth
            autoFocus
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={DOCS_LABELS.SEARCH_BOX_PLACEHOLDER}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined />
                </InputAdornment>
              ),
              sx: { borderRadius: 3, bgcolor: 'background.paper' },
            }}
            sx={{ mb: 3 }}
          />
          {renderResults()}
        </Box>

        <Card variant="outlined" sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0, borderRadius: 2 }}>
          <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTimeOutlined fontSize="small" color="action" />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {DOCS_LABELS.RECENT_ACTIVITY}
            </Typography>
          </Box>
          <Divider />
          {recent.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
              {DOCS_LABELS.RECENT_EMPTY}
            </Typography>
          ) : (
            <List disablePadding>
              {recent.map((docPage) => {
                if (!docPage) return null
                const by = fullName(docPage.updatedBy)
                return (
                  <ListItemButton key={docPage.id} onClick={() => onOpen(docPage.slug)} sx={{ px: 2, py: 1 }}>
                    <ListItemText
                      primary={
                        <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                          {docPage.icon ? `${docPage.icon} ` : ''}
                          {docPage.title || DOCS_LABELS.UNTITLED}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {dayjs(docPage.updatedAt).format('MMM D, YYYY h:mm A')}
                          {by ? ` · ${by}` : ''}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                )
              })}
            </List>
          )}
        </Card>
      </Box>
    </Box>
  )
}
